import React from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowRight, Linkedin, MapPin, ShieldCheck, Building2, Store, Apple } from "lucide-react";
import { Reveal, SectionHeading } from "@/components/common";
import { goToAnchor } from "@/lib/nav";
import Seo from "@/components/Seo";
import { FOUNDER, LEGAL_OPERATOR, STORES, STORY_36HOURS } from "@/data/company";

const REASONS = [
  {
    title: "Why WorkPay was created",
    text:
      "WorkPay began with a simple, painful truth: project-based businesses lose time and trust chasing payments, clarifying scope and resolving milestone disputes. It was built to make starting a project feel clear and professional for everyone involved.",
  },
  {
    title: "A problem lived, not imagined",
    text: STORY_36HOURS + " Delayed payments, unclear approvals and milestone disagreements were a weekly reality — so the workflow was designed to remove that friction.",
  },
  {
    title: "Built for both sides",
    text:
      "Vendors get a professional way to begin work with payment clarity. Customers get a transparent way to see project terms, track milestones and approve progress. WorkPay serves both — not one at the expense of the other.",
  },
  {
    title: "A founder-led private launch",
    text:
      "WorkPay is currently in a private, founder-led launch for selected UAE service businesses. Onboarding is hands-on so the product is shaped by real project-payment needs before a wider public launch.",
  },
];

export default function AboutPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <main data-testid="about-page" className="pt-24">
      <Seo path="/about" />

      {/* Hero */}
      <section className="relative overflow-hidden py-16 sm:py-20">
        <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-[#ecfdf3] blur-3xl opacity-80" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <span className="inline-block text-xs tracking-[0.2em] uppercase text-[#16a34a] font-bold mb-4">
              About WorkPay
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#091630] leading-[1.03]">
              Built for UAE project businesses.
            </h1>
            <p className="mt-6 text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
              WorkPay is software for managing project-payment workflows — created from real operational
              experience, and built to give vendors and customers a clearer way to work together.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founder */}
      <section className="relative pb-8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-[2rem] bg-[#f7faf8] border border-slate-100 p-6 sm:p-10 grid sm:grid-cols-[auto_1fr] gap-8 items-center">
              <img
                src={FOUNDER.photo}
                alt={`${FOUNDER.name}, Founder of WorkPay`}
                width="800"
                height="800"
                loading="lazy"
                decoding="async"
                className="h-40 w-40 sm:h-48 sm:w-48 rounded-3xl object-cover border border-slate-100 soft-shadow"
              />
              <div>
                <p className="font-display text-2xl font-bold text-[#091630]">{FOUNDER.name}</p>
                <p className="text-sm font-semibold text-[#16a34a]">{FOUNDER.titleWorkPay}</p>
                <p className="text-sm text-slate-500">{FOUNDER.titleOther}</p>
                <p className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
                  <MapPin className="h-3.5 w-3.5" /> {FOUNDER.location}
                </p>
                <div className="mt-4 space-y-3 text-[15px] text-slate-500 leading-relaxed">
                  {FOUNDER.story.map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
                <a
                  href={FOUNDER.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="about-founder-linkedin"
                  className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#091630] hover:text-[#16a34a] transition-colors"
                >
                  <Linkedin className="h-4 w-4 text-[#0a66c2]" /> Connect with {FOUNDER.name.split(" ")[0]} on LinkedIn
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Reasons */}
      <section className="relative py-14 sm:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 gap-5">
            {REASONS.map((r, i) => (
              <Reveal key={r.title} delay={(i % 2) * 0.08}>
                <div data-testid={`about-reason-${i}`} className="h-full bg-white rounded-2xl border border-slate-100 soft-shadow p-7">
                  <h2 className="font-display text-lg font-bold text-[#091630] mb-2">{r.title}</h2>
                  <p className="text-[15px] text-slate-500 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* How payments / what WorkPay is */}
      <section className="relative py-14 sm:py-20 bg-[#f7faf8]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="rounded-2xl bg-white border border-slate-100 soft-shadow p-7 sm:p-9">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-11 w-11 rounded-xl bg-[#ecfdf3] flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-[#16a34a]" />
                </div>
                <h2 className="font-display text-xl font-bold text-[#091630]">What WorkPay is (and isn't)</h2>
              </div>
              <p className="text-[15px] text-slate-500 leading-relaxed">
                WorkPay provides software for managing project-payment workflows — projects, milestones,
                approvals and documentation. Payments are processed through Stripe. WorkPay is{" "}
                <span className="font-semibold text-[#091630]">not a bank, wallet, payment gateway or escrow provider</span>,
                and does not store card details.
              </p>
              <div className="mt-5">
                <Link
                  to="/how-payments-work"
                  data-testid="about-how-payments"
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-[#16a34a] hover:gap-2.5 transition-all"
                >
                  How payments work <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </Reveal>

          {/* Store links */}
          <Reveal delay={0.1}>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={STORES.googlePlay}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-google-play"
                className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-white border border-slate-200 py-4 font-semibold text-[#091630] hover:border-slate-300 transition-colors"
              >
                <Store className="h-5 w-5 text-[#16a34a]" /> Get it on Google Play
              </a>
              <a
                href={STORES.appStore}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="about-app-store"
                className="flex-1 flex items-center justify-center gap-2 rounded-2xl bg-white border border-slate-200 py-4 font-semibold text-[#091630] hover:border-slate-300 transition-colors"
              >
                <Apple className="h-5 w-5 text-[#091630]" /> Download on the App Store
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Legal operator */}
      <section className="relative py-14 sm:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div data-testid="about-legal-operator" className="rounded-2xl border border-slate-100 bg-white soft-shadow p-6 flex items-start gap-4">
              <div className="h-11 w-11 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
                <Building2 className="h-5 w-5 text-[#091630]" />
              </div>
              <div className="text-sm text-slate-500 leading-relaxed">
                <p className="text-[#091630] font-semibold">{LEGAL_OPERATOR.sentence}</p>
                <p className="mt-2">
                  {LEGAL_OPERATOR.freeZone} · Licence No. {LEGAL_OPERATOR.license} · TRN {LEGAL_OPERATOR.trn}
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 sm:py-20 bg-[#091630] overflow-hidden">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-[#21c55e]/15 blur-3xl" />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-white">
              Join the founder-led launch.
            </h2>
            <p className="mt-4 text-slate-300 max-w-xl mx-auto">
              We're onboarding the first UAE service businesses personally. Tell us about your projects.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <button
                data-testid="about-cta"
                onClick={() => navigate("/contact")}
                className="bg-[#21c55e] text-[#091630] font-bold py-3.5 px-7 rounded-full hover:bg-white transition-colors flex items-center justify-center gap-2"
              >
                Book a Founder Demo <ArrowRight className="h-5 w-5" />
              </button>
              <button
                data-testid="about-cta-early"
                onClick={() => goToAnchor(navigate, "/about", "#early-access")}
                className="bg-transparent text-white font-semibold py-3.5 px-7 rounded-full border border-white/25 hover:bg-white/10 transition-colors"
              >
                Request Early Access
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
